import { createContext, useEffect, useState, type PropsWithChildren, useContext } from "react";
import { buildLoginRedirectUrl, isPublicAuthRoute } from "../../commons/helpers";
import TokenManager from "../../commons/TokenManager";
import useAuth from "../../services/useAuth";
import { URL_LOGIN } from "../../config";

interface IAuthContext {
    accessToken: string | undefined;
    isAuthenticated: boolean;
    setIsAuthenticated: (value: boolean) => void;
}

const AuthContext = createContext({} as IAuthContext);

export const useAuthContext = () => useContext(AuthContext);

export default function AuthContextProvider({ children }: PropsWithChildren<unknown>) {
    const [accessToken, setAccessToken] = useState<string | undefined>();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const { refreshAccessToken } = useAuth();

    useEffect(() => {
        async function refreshTokenOnLoad() {
            const isPublicRoute = isPublicAuthRoute(window.location.pathname);
            if (isPublicRoute) {
                setIsReady(true);
                return;
            }

            const credentials = await refreshAccessToken();
            const accessToken = credentials?.accessToken;
            const refreshToken = credentials?.refreshToken;

            if (accessToken && refreshToken) {
                TokenManager.setAccessToken(accessToken, refreshToken);
                setAccessToken(accessToken);
                setIsAuthenticated(true);
                setIsReady(true);
                return;
            }

            setIsAuthenticated(false);
            setAccessToken(undefined);
            setIsReady(true);
            if (window.location.pathname !== URL_LOGIN) {
                window.location.href = buildLoginRedirectUrl();
            }
        }
        refreshTokenOnLoad();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isReady) {
        return null;
    }

    return (
        <AuthContext.Provider value={{ accessToken, isAuthenticated, setIsAuthenticated }}>
            {children}
        </AuthContext.Provider>
    );
}
